import { IconButton } from '@liveauctioneers/hammer-ui-core/iconButton';
import { Close, UploadSimple } from '@liveauctioneers/hammer-ui-core/icons';
import { FormattedMessage } from '@liveauctioneers/hammer-ui-core/intl';
import { Link } from '@liveauctioneers/hammer-ui-core/link';
import { Loading } from '@liveauctioneers/hammer-ui-core/loading';
import { BodySecondary, Caption, LabelStrong } from '@liveauctioneers/hammer-ui-core/text';
import { cn } from '@liveauctioneers/hammer-ui-theme/cn';
import { useCallback } from 'react';
import { type FileRejection, useDropzone } from 'react-dropzone';
import { useNavigate } from 'react-router-dom';

import { useImageSearch } from '@/hooks/useImageSearch';
import { submitImageSearch } from '@/redux/modules/search/imageSearch/imageSearch.actions';
import { useAppFormatMessage } from '@/utils/intl/useAppFormatMessage';

const ACCEPTED_TYPES = 'image/jpeg, image/png, image/webp';
// One above NAVIGATION_DROPDOWN_Z_INDEX_LAYER (12) in HammerHeader so the popover
// sits over the nav/suggestions dropdowns.
const POPOVER_Z_INDEX = 13;

type Props = {
    onClose: () => void;
};

/**
 * Anchored popover under the search-by-image entry point: dropzone + analysing +
 * error states per the SEAR-1251 Figma. On a successful search it navigates to the
 * results page, which renders from the imageSearch slice.
 */
export const SearchByImagePopover = ({ onClose }: Props) => {
    const formatMessage = useAppFormatMessage();
    const navigate = useNavigate();
    // Stale state is cleared by the entry point before it opens this popover.
    const { error, status, submit } = useImageSearch();

    const onDrop = useCallback(
        async (acceptedFiles: File[], fileRejections: FileRejection[]) => {
            // A wrong-type rejection still goes through the thunk so its type pre-check
            // is the single source of the error state. No size cap — the client resize
            // shrinks any image, so byte size doesn't need enforcing.
            const file = acceptedFiles[0] ?? fileRejections[0]?.file;
            if (!file) {
                return;
            }
            const action = await submit(file);
            if (submitImageSearch.fulfilled.match(action)) {
                onClose();
                navigate('/visual-search');
            }
        },
        [navigate, onClose, submit]
    );

    // No `noClick` — the whole dropzone area opens the file dialog; the Browse link
    // is visual emphasis only (its click bubbles to the dropzone root).
    const { getInputProps, getRootProps, isDragActive } = useDropzone({
        accept: ACCEPTED_TYPES,
        multiple: false,
        onDrop,
    });

    const isAnalysing = status === 'analysing';
    const errorMessage =
        status === 'error'
            ? formatMessage(['visualSearch', error === 'invalid-type' ? 'typeError' : 'serverError'])
            : null;

    return (
        <div
            className="shadow-lg absolute right-0 top-[calc(100%+12px)] w-[360px] rounded-md border border-border-secondary bg-background-primary p-sm"
            data-testid="searchByImagePopover"
            style={{ zIndex: POPOVER_Z_INDEX }}
        >
            <div className="flex items-start justify-between">
                <div className="flex flex-col">
                    <LabelStrong>{formatMessage(['visualSearch', 'searchByImage'])}</LabelStrong>
                    <Caption className="text-text-secondary">
                        {formatMessage(['visualSearch', 'findSimilarSubtitle'])}
                    </Caption>
                </div>
                <IconButton
                    aria-label={String(formatMessage(['visualSearch', 'close']))}
                    hideTooltip
                    icon={Close}
                    onClick={onClose}
                    size="sm"
                    style="neutral"
                    type="button"
                    variant="transparent"
                />
            </div>

            {isAnalysing ? (
                <div
                    className="mt-sm flex flex-col items-center gap-sm py-md"
                    data-testid="searchByImageAnalysing"
                >
                    <Loading />
                    <BodySecondary>{formatMessage(['visualSearch', 'analysing'])}</BodySecondary>
                </div>
            ) : (
                <>
                    <div
                        {...getRootProps()}
                        className={cn(
                            'mt-sm flex flex-col items-center gap-2xs rounded-md border border-border-secondary px-sm py-md',
                            {
                                'bg-background-critical-subdued border-dashed border-border-critical':
                                    isDragActive || Boolean(errorMessage),
                            }
                        )}
                        data-testid="searchByImageDropzone"
                    >
                        <input {...getInputProps()} />
                        <UploadSimple size="md" />
                        <BodySecondary>
                            <FormattedMessage
                                id="visualSearch.dropzoneAction"
                                values={{
                                    browseLink: <Link key="browse">{formatMessage(['visualSearch', 'browse'])}</Link>,
                                }}
                            />
                        </BodySecondary>
                    </div>
                    {errorMessage ? (
                        <Caption className="mt-2xs text-text-critical">{errorMessage}</Caption>
                    ) : (
                        <Caption className="mt-2xs text-text-secondary">
                            {formatMessage(['visualSearch', 'fileRequirements'])}
                        </Caption>
                    )}
                </>
            )}
        </div>
    );
};
